/**
 * InvoiceHistory.jsx
 *
 * Layout: full-screen overlay split into:
 *   LEFT  — full remaining width detail area (tabs, image, tables, diffs)
 *   RIGHT — fixed 320px sidebar (list of invoices)
 *
 * Props:
 *   open     boolean
 *   onClose  () => void
 *   cafeId   string
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import styles from '../style/Invoicehistory.css';
import { host } from '../script/variables';

/* ════════════════════════════════════════════════════════════
   DATA HOOKS
═══════════════════════════════════════════════════════════ */
function useInvoiceList(cafeId, open, token, setError) {
  const [invoices, setInvoices] = useState([]);
  const [loading,  setLoading]  = useState(false);

  useEffect(() => {
    if (!open || !cafeId) return;
    let cancelled = false;

    const run = async () => {
      try {
        setLoading(true);
        setError(null);
        const r = await fetch(`${host}/api/inventory/getAiInvoice`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: token },
          body: JSON.stringify({ cafeid: cafeId }),
        });
        const res = await r.json();
        if (!r.ok) throw new Error(res?.msg || 'Failed to fetch invoices');
        if (!cancelled) setInvoices(res.data || []);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [cafeId, open, token]);

  return { invoices, loading };
}

function useInvoiceDetail(cafeId, invoiceId, token, setError) {
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading,        setLoading]        = useState(false);

  useEffect(() => {
    if (!invoiceId || !cafeId) return;
    let cancelled = false;

    const run = async () => {
      try {
        setLoading(true);
        setError(null);
        const r = await fetch(`${host}/api/inventory/getFinalInventories`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: token },
          body: JSON.stringify({ cafeid: cafeId, invoiceId }),
        });
        const res = await r.json();
        if (!r.ok) throw new Error(res?.msg || 'Failed to fetch detail');
        if (!cancelled) setInventoryItems(res.data || []);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [cafeId, invoiceId, token]);

  return { inventoryItems, loading };
}

/* ════════════════════════════════════════════════════════════
   UTILITIES
═══════════════════════════════════════════════════════════ */
const normalizeName = (name) => {
  if (!name || typeof name !== 'string') return '';
  return name.trim().toLowerCase();
};

const fmt = {
  date: (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  },
  time: (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  },
  datetime: (iso) => iso ? `${fmt.date(iso)}, ${fmt.time(iso)}` : '—',
  rupee:    (n)   => n != null ? `₹${Number(n).toLocaleString('en-IN')}` : '—',
  relative: (iso) => {
    if (!iso) return 'Unknown';
    const diff = Date.now() - new Date(iso).getTime();
    if (diff < 60000)     return 'Just now';
    if (diff < 3600000)   return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000)  return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 172800000) return 'Yesterday';
    return fmt.date(iso);
  },
  idShort: (id) => String(id || '').slice(-8).toUpperCase(),
};

/* ════════════════════════════════════════════════════════════
   DIFF / PAIR HOOKS
═══════════════════════════════════════════════════════════ */
function usePairs(invoice, inventoryItems) {
  return useMemo(() => {
    if (!invoice || !inventoryItems?.length) return [];
    return (invoice.parsedResponse || []).map((ai) => {
      const norm  = normalizeName(ai.name);
      const final = inventoryItems.find(
        (it) => normalizeName(it.normalizedName) === norm || normalizeName(it.name) === norm
      );
      return { ai, final: final || null };
    });
  }, [invoice, inventoryItems]);
}

function useDiffs(invoice, inventoryItems) {
  return useMemo(() => {
    if (!invoice || !inventoryItems?.length) return [];
    return (invoice.parsedResponse || []).flatMap((ai) => {
      const norm  = normalizeName(ai.name);
      const final = inventoryItems.find(
        (it) => normalizeName(it.normalizedName) === norm || normalizeName(it.name) === norm
      );
      if (!final) return [];
      const changes = [];
      if (ai.unitPrice != null && final.unitPrice != null && Number(ai.unitPrice) !== Number(final.unitPrice))
        changes.push({ field: 'Price',    old: fmt.rupee(ai.unitPrice),        new: fmt.rupee(final.unitPrice) });
      if (ai.quantity  != null && final.quantity  != null && Number(ai.quantity)  !== Number(final.quantity))
        changes.push({ field: 'Qty',      old: String(ai.quantity),            new: String(final.quantity) });
      if (ai.unit && final.unit && ai.unit !== final.unit)
        changes.push({ field: 'Unit',     old: ai.unit,                        new: final.unit });
      if (ai.category && final.category && ai.category.replace(/_/g,' ') !== final.category.replace(/_/g,' '))
        changes.push({ field: 'Category', old: ai.category.replace(/_/g,' '), new: final.category.replace(/_/g,' ') });
      return changes.length ? [{ name: ai.name, changes }] : [];
    });
  }, [invoice, inventoryItems]);
}

// Lightweight diff count for sidebar cards (no inventoryItems needed — uses invoice list only)
function diffCountFromInvoice(invoice, inventoryItems) {
  if (!invoice || !inventoryItems?.length) return 0;
  return (invoice.parsedResponse || []).filter((ai) => {
    const norm  = normalizeName(ai.name);
    const final = inventoryItems.find(
      (it) => normalizeName(it.normalizedName) === norm || normalizeName(it.name) === norm
    );
    return final && (
      (ai.unitPrice != null && final.unitPrice != null && Number(ai.unitPrice) !== Number(final.unitPrice)) ||
      (ai.quantity  != null && final.quantity  != null && Number(ai.quantity)  !== Number(final.quantity))
    );
  }).length;
}

/* ════════════════════════════════════════════════════════════
   SHARED ATOMS
═══════════════════════════════════════════════════════════ */
function Badge({ status }) {
  const cls   = status === 'success' ? styles.badgeSuccess : status === 'pending' ? styles.badgePending : styles.badgeError;
  const label = status === 'success' ? '✓ Processed'       : status === 'pending' ? '⏳ Pending'        : '✕ Error';
  return <span className={`${styles.badge} ${cls}`}>{label}</span>;
}

function SectionLabel({ children }) {
  return <div className={styles.sectionLabel}>{children}</div>;
}

function FieldRow({ label, value }) {
  return (
    <div className={styles.fieldRow}>
      <span className={styles.fieldKey}>{label}</span>
      <span className={styles.fieldVal}>{value ?? '—'}</span>
    </div>
  );
}

function MiniField({ k, v }) {
  if (v == null || v === '') return null;
  return (
    <div className={styles.miniField}>
      <span className={styles.miniFieldKey}>{k}: </span>
      <span className={styles.miniFieldVal}>{String(v)}</span>
    </div>
  );
}

function EmptyMsg({ icon, title, sub }) {
  return (
    <div className={styles.emptyState}>
      <div className={styles.emptyIcon}>{icon}</div>
      {title && <div className={styles.emptyTitle}>{title}</div>}
      {sub   && <div className={styles.emptySub}>{sub}</div>}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   TAB PANELS
═══════════════════════════════════════════════════════════ */

/* ── Image Tab ────────────────────────────────────────────── */
function ImageTab({ invoice }) {
  const [imgErr, setImgErr] = useState(false);
  const [zoom, setZoom] = useState(1);

  const isPdf = invoice?.imageUrl?.toLowerCase().includes('.pdf');

  const handleZoomIn = () => setZoom((z) => Math.min(z + 0.2, 3));
  const handleZoomOut = () => setZoom((z) => Math.max(z - 0.2, 0.5));
  const handleReset = () => setZoom(1);

  return (
    <div className={styles.imageTabGrid}>
      
      {/* LEFT: Viewer */}
      <div className={styles.imageViewer}>
        
        <div className={styles.imageViewerHeader}>
          <div className={styles.imageViewerLabel}>
            <span>🖼</span> Original Invoice
          </div>

          {/* Actions */}
          <div className={styles.viewerActions}>
            
            {/* Download */}
            {invoice?.imageUrl && (
              <a
                href={invoice.imageUrl}
                download
                target="_blank"
                rel="noreferrer"
                className={styles.downloadBtn}
                title="Download"
              >
                ⬇️
              </a>
            )}

            {/* Zoom controls (only for image) */}
            {!isPdf && !imgErr && (
              <>
                <button onClick={handleZoomOut} className={styles.zoomBtn}>−</button>
                <button onClick={handleReset} className={styles.zoomBtn}>Reset</button>
                <button onClick={handleZoomIn} className={styles.zoomBtn}>+</button>
              </>
            )}
          </div>
        </div>

        {/* CONTENT */}
        {invoice?.imageUrl ? (
          isPdf ? (
            <iframe
              src={invoice.imageUrl}
              style={{ width: '100%', height: '520px', border: 'none' }}
              title="Invoice PDF"
            />
          ) : !imgErr ? (
            <img
              className={styles.invoiceImg}
              style={{
                transform: `scale(${zoom})`,
                transformOrigin: 'top center',
                transition: '200ms',
              }}
              src={invoice.imageUrl}
              alt="Invoice"
              onError={() => setImgErr(true)}
            />
          ) : (
            <div className={styles.imgNoUrl}>
              ⚠ Could not load image
            </div>
          )
        ) : (
          <div className={styles.imgNoUrl}>
            📄 No image URL stored for this invoice
          </div>
        )}
      </div>

      {/* RIGHT: Info */}
      <div className={styles.infoCard}>
        <div className={styles.infoCardTitle}>File Info</div>
        <FieldRow label="File Key" value={invoice.fileKey} />
        <FieldRow label="Provider" value={invoice.provider} />
        <FieldRow label="Invoice ID" value={String(invoice.invoiceId || invoice._id || '—')} />
        <FieldRow label="Created" value={fmt.datetime(invoice.createdAt)} />
        <FieldRow label="Status" value={invoice.status} />
      </div>
    </div>
  );
}

/* ── Extracted Tab ────────────────────────────────────────── */
function ExtractedTab({ invoice }) {
  const items = invoice.parsedResponse || [];
  if (!items.length) return <EmptyMsg icon="📄" sub="No extracted data available." />;

  return (
    <>
      <SectionLabel>AI Extracted — {items.length} Items</SectionLabel>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Qty</th>
              <th>Unit</th>
              <th>Pack</th>
              <th>Price / Unit</th>
              <th>Category</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={it._id || i}>
                <td style={{ color: 'var(--textMuted)', width: 32 }}>{i + 1}</td>
                <td className={styles.itemName}>{it.name || '—'}</td>
                <td>{it.quantity ?? '—'}</td>
                <td>{it.unit || '—'}</td>
                <td>
                  {it.baseQuantityPerUnit
                    ? <span style={{ background: 'var(--surface3)', border: '1px solid var(--border)', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500 }}>
                        {it.baseQuantityPerUnit}{it.baseUnit || ''}/pc
                      </span>
                    : <span style={{ color: 'var(--textMuted)' }}>—</span>}
                </td>
                <td style={{ fontWeight: 600 }}>{it.unitPrice != null ? fmt.rupee(it.unitPrice) : '—'}</td>
                <td><span className={styles.catPill}>{(it.category || '—').replace(/_/g, ' ')}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/* ── Final Tab ────────────────────────────────────────────── */
function FinalTab({ invoice, inventoryItems, loading }) {
  const pairs = usePairs(invoice, inventoryItems);

  if (loading) {
    return (
      <div className={styles.detailLoading}>
        <div className={styles.spinner} />
        <div className={styles.spinnerText}>Loading final data…</div>
      </div>
    );
  }

  if (!pairs.length) return <EmptyMsg icon="📄" sub="No final data linked." />;

  return (
    <>
      <SectionLabel>Final Saved Data — {pairs.length} Items</SectionLabel>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Qty</th>
              <th>Unit</th>
              <th>Price / Unit</th>
              <th>Min Qty</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {pairs.map(({ ai, final }, i) => (
              <tr key={i}>
                <td style={{ color: 'var(--textMuted)', width: 32 }}>{i + 1}</td>
                <td className={styles.itemName}>
                  {final ? (final.name || ai.name) : ai.name}
                  {!final && <span className={styles.noMatch}>⚠ No Match</span>}
                </td>
                <td>{final?.quantity ?? '—'}</td>
                <td>{final ? (final.unit || final.baseUnit || '—') : '—'}</td>
                <td style={{ fontWeight: 600 }}>{final?.unitPrice != null ? fmt.rupee(final.unitPrice) : '—'}</td>
                <td>{final ? (final.lowStockThreshold ?? final.minQty ?? '—') : '—'}</td>
                <td>
                  {final
                    ? <span style={{ color: 'var(--green)', fontWeight: 600, fontSize: 12 }}>✓ Saved</span>
                    : <span style={{ color: 'var(--textMuted)', fontSize: 12 }}>—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SectionLabel>Side-by-Side Compare</SectionLabel>
      <div className={styles.compareGrid}>
        {pairs.map(({ ai, final }, i) => {
          const changed = final && (
            (ai.unitPrice != null && final.unitPrice != null && Number(ai.unitPrice) !== Number(final.unitPrice)) ||
            (ai.quantity  != null && final.quantity  != null && Number(ai.quantity)  !== Number(final.quantity))
          );
          return (
            <div key={i} className={`${styles.compareCard} ${changed ? styles.compareCardChanged : ''}`}>
              {changed && <span className={styles.editedBadge}>Edited</span>}
              <div className={styles.compareCardName}>{ai.name}</div>
              <div className={styles.compareCols}>
                <div>
                  <div className={`${styles.compareColHead} ${styles.compareColHeadAi}`}>🤖 AI Extracted</div>
                  <MiniField k="Qty"   v={ai.quantity} />
                  <MiniField k="Price" v={ai.unitPrice != null ? fmt.rupee(ai.unitPrice) : null} />
                  <MiniField k="Unit"  v={ai.unit} />
                </div>
                <div>
                  <div className={`${styles.compareColHead} ${styles.compareColHeadFinal}`}>✅ Final Saved</div>
                  <MiniField k="Qty"   v={final?.quantity ?? '—'} />
                  <MiniField k="Price" v={final?.unitPrice != null ? fmt.rupee(final.unitPrice) : '—'} />
                  <MiniField k="Unit"  v={final?.unit ?? '—'} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

/* ── Diff Tab ─────────────────────────────────────────────── */
function DiffTab({ invoice, inventoryItems, loading }) {
  const diffs = useDiffs(invoice, inventoryItems);

  if (loading) {
    return (
      <div className={styles.detailLoading}>
        <div className={styles.spinner} />
        <div className={styles.spinnerText}>Loading changes…</div>
      </div>
    );
  }

  if (!diffs.length) {
    return (
      <EmptyMsg
        icon="✅"
        title="No Changes Detected"
        sub="All AI-extracted values match the final saved inventory data exactly."
      />
    );
  }

  return (
    <>
      <SectionLabel>
        Edited Fields — {diffs.length} Item{diffs.length !== 1 ? 's' : ''}
      </SectionLabel>
      <div className={styles.diffList}>
        {diffs.map((d, i) => (
          <div key={i} className={styles.diffItem}>
            <div className={styles.diffItemHeader}>
              <div className={styles.diffItemName}>{d.name}</div>
              <span className={styles.changesBadge}>
                {d.changes.length} change{d.changes.length !== 1 ? 's' : ''}
              </span>
            </div>
            {d.changes.map((c, j) => (
              <div key={j} className={styles.diffRow}>
                <span className={styles.diffKey}>{c.field}</span>
                <span className={styles.diffOld}>{c.old}</span>
                <span className={styles.diffArrow}>→</span>
                <span className={styles.diffNew}>{c.new}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </>
  );
}

/* ════════════════════════════════════════════════════════════
   INVOICE CARD (sidebar)
═══════════════════════════════════════════════════════════ */
function InvoiceCard({ invoice, active, onClick }) {
  const items    = invoice.parsedResponse || [];
  const preview  = items.slice(0, 2).map((i) => i.name).join(', ') + (items.length > 2 ? ` +${items.length - 2}` : '');
  const totalVal = items.reduce((s, i) => s + Number(i.unitPrice || 0) * Number(i.quantity || 1), 0);

  return (
    <div
      className={`${styles.card} ${active ? styles.cardActive : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
    >
      {active && <div className={styles.cardActiveLine} />}
      <div className={styles.cardTop}>
        <div className={styles.cardMeta}>
          <div className={styles.cardId}>#{fmt.idShort(invoice._id)}</div>
          <div className={styles.cardPreview} title={preview}>{preview || '—'}</div>
          <div className={styles.cardCount}>
            {items.length} item{items.length !== 1 ? 's' : ''}
          </div>
        </div>
        <Badge status={invoice.status || 'success'} />
      </div>
      <div className={styles.cardFooter}>
        <span className={styles.cardTime}><span>🕐</span>{fmt.relative(invoice.createdAt)}</span>
        <span className={styles.cardValue}>{totalVal ? fmt.rupee(totalVal) : '—'}</span>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   DETAIL AREA (full left width)
═══════════════════════════════════════════════════════════ */
const TABS = [
  { id: 'image',     label: '🖼 Invoice Image' },
  { id: 'extracted', label: '🤖 AI Extracted'  },
  { id: 'final',     label: '✅ Final Data'     },
  { id: 'diff',      label: '⚡ Changes'        },
];

function DetailArea({ invoice, inventoryItems, detailLoading, diffs }) {
  const [activeTab, setActiveTab] = useState('image');

  useEffect(() => { setActiveTab('image'); }, [invoice?._id]);

  return (
    <div className={styles.detailArea}>
      {/* Header */}
      <div className={styles.detailHeader}>
        <div className={styles.detailHeaderTop}>
          <div className={styles.detailHeaderLeft}>
            <div className={styles.detailInvoiceLabel}>
              Invoice #{fmt.idShort(invoice._id)}
            </div>
            <div className={styles.detailTitle}>
              {(invoice.parsedResponse || []).length} items extracted
            </div>
          </div>
        </div>

        <div className={styles.detailChips}>
          <span className={styles.chip}>🕐 {fmt.datetime(invoice.createdAt)}</span>
          <Badge status={invoice.status || 'success'} />
          {invoice.provider && (
            <span className={styles.chip}>🤖 {invoice.provider}</span>
          )}
          {diffs.length > 0 && (
            <span className={`${styles.chip} ${styles.chipAmber}`}>
              ⚡ {diffs.length} edited
            </span>
          )}
        </div>

        {/* Tabs */}
        <div className={styles.tabs}>
          {TABS.map((t) => (
            <button
              key={t.id}
              className={`${styles.tab} ${activeTab === t.id ? styles.tabActive : ''}`}
              onClick={() => setActiveTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Scrollable body */}
      <div className={styles.detailBody}>
        {activeTab === 'image'     && <ImageTab     invoice={invoice} />}
        {activeTab === 'extracted' && <ExtractedTab invoice={invoice} />}
        {activeTab === 'final'     && (
          <FinalTab invoice={invoice} inventoryItems={inventoryItems} loading={detailLoading} />
        )}
        {activeTab === 'diff'      && (
          <DiffTab  invoice={invoice} inventoryItems={inventoryItems} loading={detailLoading} />
        )}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   ROOT — default export
═══════════════════════════════════════════════════════════ */
export default function InvoiceHistory({ open, onClose, cafeId }) {
  const token   = localStorage.getItem('partnertoken');
  const [error, setError]   = useState(null);

  const { invoices, loading }             = useInvoiceList(cafeId, open, token, setError);
  const [selectedId,       setSelectedId] = useState(null);
  const [selectedInvoiceId, setSelInvId]  = useState(null);
  const [searchQ, setSearchQ]             = useState('');
  const overlayRef = useRef(null);

  const { inventoryItems, loading: detailLoading } = useInvoiceDetail(
    cafeId, selectedInvoiceId, token, setError
  );

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key !== 'Escape') return;
      if (selectedId) { setSelectedId(null); setSelInvId(null); }
      else onClose?.();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, selectedId, onClose]);

  // Body scroll lock
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Reset on close
  useEffect(() => {
    if (!open) { setSelectedId(null); setSelInvId(null); setSearchQ(''); }
  }, [open]);

  const filtered = useMemo(() => {
    if (!searchQ.trim()) return invoices;
    const q = searchQ.toLowerCase();
    return invoices.filter((inv) =>
      (inv.parsedResponse || []).some(
        (it) => normalizeName(it.name).includes(q) || (it.category || '').toLowerCase().includes(q)
      ) || String(inv._id || '').toLowerCase().includes(q)
    );
  }, [invoices, searchQ]);

  const totalItems = useMemo(
    () => invoices.reduce((s, inv) => s + (inv.parsedResponse || []).length, 0),
    [invoices]
  );

  const groups = useMemo(() => {
    const map = {};
    filtered.forEach((inv) => {
      const key = new Date(inv.createdAt || Date.now()).toDateString();
      if (!map[key]) map[key] = { label: fmt.date(inv.createdAt), items: [] };
      map[key].items.push(inv);
    });
    return Object.values(map);
  }, [filtered]);

  const selectedInvoice = useMemo(
    () => invoices.find((i) => i._id === selectedId) || null,
    [invoices, selectedId]
  );

  const diffs = useDiffs(selectedInvoice, inventoryItems);

  if (!open) return null;

  return (
    <div
      className={styles.overlay}
      ref={overlayRef}
      onClick={(e) => { if (e.target === overlayRef.current) onClose?.(); }}
      role="dialog"
      aria-modal="true"
      aria-label="Invoice History"
    >
      <div className={styles.layout}>

        {/* ── LEFT: Detail area ─────────────────────────────── */}
        {selectedInvoice ? (
          <DetailArea
            invoice={selectedInvoice}
            inventoryItems={inventoryItems}
            detailLoading={detailLoading}
            diffs={diffs}
          />
        ) : (
          <div className={styles.detailArea}>
            <div className={styles.detailEmpty}>
              <div className={styles.detailEmptyIllustration}>📋</div>
              <div className={styles.detailEmptyTitle}>Select an Invoice</div>
              <div className={styles.detailEmptySub}>
                Pick an invoice from the list on the right to view its AI-extracted data, final saved values, and any changes.
              </div>
            </div>
          </div>
        )}

        {/* ── RIGHT: Sidebar list ───────────────────────────── */}
        <div className={styles.sidebar}>
          {/* Header */}
          <div className={styles.sidebarHeader}>
            <div className={styles.sidebarHeaderRow}>
              <div className={styles.sidebarTitle}>
                <div className={styles.titleIcon}>📋</div>
                Invoice History
              </div>
              <button className={styles.closeBtn} onClick={onClose} aria-label="Close">✕</button>
            </div>

            <div className={styles.searchWrap}>
              <span className={styles.searchIcon}>🔍</span>
              <input
                className={styles.searchInput}
                type="text"
                placeholder="Search invoices or items…"
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
              />
            </div>

            <div className={styles.statsRow}>
              <div className={styles.statChip}>
                <div className={styles.statNum}>{loading ? '…' : invoices.length}</div>
                <div className={styles.statLbl}>Invoices</div>
              </div>
              <div className={styles.statChip}>
                <div className={styles.statNum}>{loading ? '…' : totalItems}</div>
                <div className={styles.statLbl}>Items</div>
              </div>
              <div className={styles.statChip}>
                <div className={styles.statNum}>
                  {loading ? '…' : invoices.filter(inv =>
                    (inv.parsedResponse || []).some(ai => {
                      const norm = normalizeName(ai.name);
                      return inventoryItems.some(it =>
                        (normalizeName(it.normalizedName) === norm || normalizeName(it.name) === norm) &&
                        ((ai.unitPrice != null && it.unitPrice != null && Number(ai.unitPrice) !== Number(it.unitPrice)) ||
                         (ai.quantity  != null && it.quantity  != null && Number(ai.quantity)  !== Number(it.quantity)))
                      );
                    })
                  ).length}
                </div>
                <div className={styles.statLbl}>Edited</div>
              </div>
            </div>
          </div>

          {/* List */}
          <div className={styles.list}>
            {loading ? (
              [1, 2, 3, 4].map((n) => (
                <div key={n} className={styles.card} style={{ pointerEvents: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div className={styles.skeleton} style={{ height: 10, width: '40%' }} />
                  <div className={styles.skeleton} style={{ height: 13, width: '85%' }} />
                  <div className={styles.skeleton} style={{ height: 10, width: '55%' }} />
                  <div style={{ height: 1, background: 'var(--border)', marginTop: 4 }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div className={styles.skeleton} style={{ height: 10, width: '35%' }} />
                    <div className={styles.skeleton} style={{ height: 10, width: '25%' }} />
                  </div>
                </div>
              ))
            ) : error ? (
              <EmptyMsg icon="⚠️" title="Failed to load" sub={error} />
            ) : !groups.length ? (
              <EmptyMsg
                icon={searchQ ? '🔍' : '📭'}
                title={searchQ ? 'No results found' : 'No invoices yet'}
                sub={searchQ
                  ? 'Try a different search term.'
                  : 'Import an invoice using Smart Import to see AI-extracted history here.'}
              />
            ) : (
              groups.map((group) => (
                <React.Fragment key={group.label}>
                  <div className={styles.dateGroup}>{group.label}</div>
                  {group.items.map((inv) => (
                    <InvoiceCard
                      key={inv._id}
                      invoice={inv}
                      active={selectedId === inv._id}
                      onClick={() => {
                        setSelectedId(inv._id);
                        setSelInvId(inv.invoiceId);
                      }}
                    />
                  ))}
                </React.Fragment>
              ))
            )}
          </div>
        </div>

      </div>
    </div>
  );
}